import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Button, Chip, Fab, Skeleton } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import BoltIcon from '@mui/icons-material/Bolt';
import QuizDialog from './QuizDialog';
import * as api from './api';

const TOPIC_COLORS = {
  'Fundamentals': '#4edba1',
  'Pharmacology': '#f0a856',
  'Med-Surg': '#6eb5ff',
  'Pediatrics': '#e88bf0',
  'Psych/Mental Health': '#c49bff',
  'Maternity': '#ff8fa3',
  'Infection Control': '#56d4c8',
  'Electrolytes/Endocrine': '#ffd66e',
};

export default function Dashboard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diagOpen, setDiagOpen] = useState(false);
  const [sessOpen, setSessOpen] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const d = await api.getDashboard();
      setData(d);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleStartDiagnostic = async () => {
    setDiagOpen(true);
    setQuizLoading(true);
    try {
      const res = await api.startDiagnostic();
      setQuizQuestions(res.questions);
    } catch (err) { console.error(err); }
    finally { setQuizLoading(false); }
  };

  const handleStartSession = async () => {
    setSessOpen(true);
    setQuizLoading(true);
    try {
      const res = await api.startSession();
      if (res.already_done) {
        setQuizQuestions([]);
        setSessOpen(false);
        alert(res.message || 'Come back tomorrow!');
        return;
      }
      setQuizQuestions(res.questions);
    } catch (err) { console.error(err); }
    finally { setQuizLoading(false); }
  };

  const handleDiagClose = (results) => {
    setDiagOpen(false);
    setQuizQuestions([]);
    if (results) loadDashboard();
  };

  const handleSessClose = (results) => {
    setSessOpen(false);
    setQuizQuestions([]);
    if (results) loadDashboard();
  };

  const scores = data?.topic_scores || {};
  const sortedTopics = Object.entries(scores)
    .map(([topic, d]) => ({ topic, pct: d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0, correct: d.correct, total: d.total }))
    .sort((a, b) => a.pct - b.pct);

  const firstName = user?.signInDetails?.loginId?.split('@')[0] || 'there';

  if (loading) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', px: 3, py: 5 }}>
        <Skeleton variant="rounded" height={80} sx={{ mb: 3, borderRadius: 3, bgcolor: 'rgba(78,219,161,0.06)' }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 2, borderRadius: 3, bgcolor: 'rgba(78,219,161,0.06)' }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3, bgcolor: 'rgba(78,219,161,0.06)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', px: 3, py: 4, pb: 12, minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', sm: '2.2rem' }, mb: 0.5 }}>
          Hey, {firstName}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
          {data?.diagnostic_complete ? 'Your NCLEX dashboard' : "Let's start with a diagnostic quiz"}
        </Typography>
      </Box>

      {data?.diagnostic_complete && (
        <Paper sx={{ mb: 3, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, rgba(78,219,161,0.08) 0%, rgba(78,219,161,0.02) 100%)', borderColor: 'rgba(78,219,161,0.12)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BoltIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                {data.streak_count || 0} day streak
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                {data.last_session_date ? `Last session: ${data.last_session_date} UTC` : 'Start your first session!'}
              </Typography>
            </Box>
          </Box>
          <Chip label="🔥" sx={{ fontSize: '1.2rem', bgcolor: 'rgba(240,168,86,0.12)' }} />
        </Paper>
      )}

      {!data?.diagnostic_complete && (
        <Paper sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, rgba(78,219,161,0.06) 0%, rgba(45,184,122,0.02) 100%)', borderColor: 'rgba(78,219,161,0.12)' }}>
          <Typography variant="h5" sx={{ mb: 1.5, fontSize: '1.25rem' }}>Diagnostic Quiz</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 3, maxWidth: 360, mx: 'auto' }}>
            10 questions across all NCLEX topics. This identifies your weak areas so we can personalize your daily sessions.
          </Typography>
          <Button variant="contained" size="large" onClick={handleStartDiagnostic} sx={{ px: 5 }}>
            Start Diagnostic
          </Button>
        </Paper>
      )}

      {data?.diagnostic_complete && sortedTopics.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderColor: 'rgba(78,219,161,0.08)' }}>
          <Typography variant="h6" sx={{ mb: 2.5, fontSize: '1rem' }}>Topic Breakdown</Typography>
          {sortedTopics.map(({ topic, pct, correct, total }) => (
            <Box key={topic} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 500 }}>{topic}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {correct}/{total} · {pct}%
                </Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, background: 'rgba(78,219,161,0.08)', overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%', borderRadius: 3, width: `${pct}%`,
                  background: `linear-gradient(90deg, ${TOPIC_COLORS[topic] || '#4edba1'}, ${TOPIC_COLORS[topic] || '#4edba1'}88)`,
                  transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                }} />
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {data?.diagnostic_complete && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button size="small" onClick={handleStartDiagnostic}
            sx={{ color: 'text.secondary', fontSize: '0.8rem', textTransform: 'none', '&:hover': { color: 'primary.main' } }}>
            Retake diagnostic
          </Button>
        </Box>
      )}

      {data?.diagnostic_complete && (
        <Fab variant="extended" color="primary" onClick={handleStartSession}
          sx={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            px: 4, py: 3.5, fontSize: '1rem', fontWeight: 600,
            background: 'linear-gradient(135deg, #4edba1 0%, #2db87a 100%)',
            boxShadow: '0 8px 32px rgba(78,219,161,0.35)',
            '&:hover': { boxShadow: '0 10px 40px rgba(78,219,161,0.5)' } }}>
          <PlayArrowRoundedIcon sx={{ mr: 1 }} />
          Today's Session
        </Fab>
      )}

      <QuizDialog open={diagOpen} onClose={handleDiagClose} title="Diagnostic Quiz" questions={quizQuestions} loading={quizLoading} onSubmit={api.submitDiagnostic} />
      <QuizDialog open={sessOpen} onClose={handleSessClose} title="Daily Session" questions={quizQuestions} loading={quizLoading} onSubmit={api.submitSession} />
    </Box>
  );
}
